/**
 * HOOK useConfirmDialog — Incapsula lo stato di un dialog di conferma.
 *
 * Uso:
 *   const { isOpen, message, options, requestConfirm, confirm, cancel } = useConfirmDialog();
 *   // Conferma distruttiva (default, pulsante 'error'):
 *   requestConfirm('Eliminare questo elemento?', () => dispatch(deleteX(id)));
 *   // Conferma non-distruttiva (per-richiesta): passa le options come 3° arg.
 *   requestConfirm('Creare una nuova versione?', () => dispatch(newVersion(id)),
 *     { confirmColor: 'primary', confirmLabel: 'Crea versione' });
 *   // Conferma IRREVERSIBILE: il pulsante si accende solo ridigitando ELIMINA.
 *   requestConfirm('Eliminare definitivamente?', () => dispatch(deleteX(id)),
 *     { requireTypedConfirmation: true });
 *   // Nel render, propaga le options con lo spread così una sola istanza serve
 *   // sia conferme distruttive sia non-distruttive:
 *   <ConfirmDialog open={isOpen} message={message} {...options}
 *     onConfirm={confirm} onCancel={cancel} />
 */

import { useState, useCallback, useRef } from 'react';

import type { ConfirmDialogOptions } from './ConfirmDialog';

export function useConfirmDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [options, setOptions] = useState<ConfirmDialogOptions | undefined>(undefined);
  const callbackRef = useRef<(() => void) | null>(null);

  const requestConfirm = useCallback(
    (msg: string, onConfirm: () => void, opts?: ConfirmDialogOptions) => {
      setMessage(msg);
      setOptions(opts);
      callbackRef.current = onConfirm;
      setIsOpen(true);
    },
    [],
  );

  const confirm = useCallback(() => {
    callbackRef.current?.();
    callbackRef.current = null;
    setOptions(undefined);
    setIsOpen(false);
  }, []);

  const cancel = useCallback(() => {
    callbackRef.current = null;
    setOptions(undefined);
    setIsOpen(false);
  }, []);

  return { isOpen, message, options, requestConfirm, confirm, cancel };
}
